import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatDate, formatEUR, errText } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useRef } from "react";
import { UploadCloud, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CustomerModules } from "@/components/CustomerModules";

const statusMap = {
  pending_approval: ["In attesa", "bg-amber-100 text-amber-900 border-amber-300"],
  demo: ["Demo", "bg-sky-100 text-sky-900 border-sky-300"],
  active: ["Attivo", "bg-emerald-100 text-emerald-900 border-emerald-300"],
  suspended: ["Sospeso", "bg-slate-200 text-slate-800 border-slate-400"],
  expired: ["Scaduto", "bg-rose-100 text-rose-900 border-rose-300"],
};
const isDemo = (c) => c.status === "demo" || (c.status === "suspended" && c.suspended_reason === "demo_scaduta");

const Section = ({ title, hint, rows, testid, renderRow, dateLabel }) => (
  <div className="space-y-2" data-testid={testid}>
    <div><h2 className="text-base md:text-lg font-semibold">{title} <span className="text-slate-400 font-normal">({rows.length})</span></h2>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
    <div className="overflow-x-auto rounded-xl border border-[#E2DDD5] bg-white">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
          <tr><th className="text-left px-3 py-3">Cliente</th><th className="text-left px-3 py-3">Prodotto</th><th className="text-left px-3 py-3">Moduli</th><th className="text-left px-3 py-3">Stato</th><th className="text-left px-3 py-3">{dateLabel}</th><th /></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-5 text-center text-slate-500">Nessun cliente.</td></tr>}
          {rows.map(renderRow)}
        </tbody>
      </table>
    </div>
  </div>
);

export default function ResellerCustomers() {
  const api = useApi();
  const inputRefs = useRef({});
  const { data = [], isLoading, refetch } = useQuery({ queryKey: ["reseller-customers"], queryFn: async () => (await api.get("/reseller/customers")).data });
  const { data: products = [] } = useQuery({ queryKey: ["reseller-products"], queryFn: async () => (await api.get("/reseller/products")).data });
  const { data: dash, refetch: refetchDash } = useQuery({ queryKey: ["reseller-dashboard"], queryFn: async () => (await api.get("/reseller/dashboard")).data });
  const prodMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const refresh = () => { refetch(); refetchDash(); };

  const openPdf = async (id) => {
    const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const uploadSigned = async (customerId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/reseller/customers/${customerId}/signed-order`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Modulo firmato caricato — in attesa di approvazione");
      refetch();
    } catch (e) { toast.error("Errore"); }
  };
  const confirmCustomer = async (c) => {
    if (!window.confirm("Confermare l'attivazione del cliente? Da questo momento matura la provvigione e parte la licenza annuale.")) return;
    try { const r = await api.post(`/reseller/customers/${c.id}/confirm-activation`); toast.success(`Cliente attivato${r.data.commission ? ` · provvigione ${formatEUR(r.data.commission)}` : ""}`); refresh(); } catch (e) { toast.error(errText(e)); }
  };

  const row = (dateOf) => (c) => {
    const prod = prodMap[c.product_id];
    const expiredDemo = c.status === "suspended" && c.suspended_reason === "demo_scaduta";
    const [label, cls] = expiredDemo ? ["Demo scaduta", statusMap.suspended[1]] : statusMap[c.status] || [c.status, "bg-slate-100 text-slate-800 border-slate-300"];
    return (
      <tr key={c.id} className="border-t border-[#E2DDD5] align-top" data-testid={`cust-${c.id}`}>
        <td className="px-3 py-3"><div className="font-medium">{c.dati_fiscali?.ragione_sociale || `${c.anagrafica.nome} ${c.anagrafica.cognome}`}</div><div className="text-xs text-slate-500">{c.anagrafica.email}</div></td>
        <td className="px-3 py-3">{prod?.name || c.product_id}</td>
        <td className="px-3 py-3"><CustomerModules api={api} c={c} prod={prod} onDone={refresh} /></td>
        <td className="px-3 py-3"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`} data-testid={`cust-status-${c.id}`}>{label}</span></td>
        <td className="px-3 py-3 text-slate-500">{formatDate(dateOf(c))}</td>
        <td className="px-3 py-3 text-right whitespace-nowrap">
          {isDemo(c) && <Button size="sm" onClick={() => confirmCustomer(c)} className="bg-[#206342] hover:bg-[#17502F] text-white mr-1" data-testid={`btn-confirm-customer-${c.id}`}><CheckCircle2 size={14} className="mr-1" /> Conferma attivazione</Button>}
          {c.order_pdf_id && <Button variant="ghost" size="sm" onClick={() => openPdf(c.order_pdf_id)} data-testid={`btn-pdf-${c.id}`}><Download size={14} className="mr-1" /> Modulo</Button>}
          {c.status === "pending_approval" && !c.signed_order_pdf_id && (
            <>
              <input type="file" accept="application/pdf,image/*" className="hidden" ref={(el) => (inputRefs.current[c.id] = el)} onChange={(e) => e.target.files?.[0] && uploadSigned(c.id, e.target.files[0])} />
              <Button variant="outline" size="sm" onClick={() => inputRefs.current[c.id]?.click()} data-testid={`btn-upload-signed-${c.id}`} className="ml-1"><UploadCloud size={14} className="mr-1" /> Carica firmato</Button>
            </>
          )}
        </td>
      </tr>
    );
  };

  const demo = data.filter(isDemo);
  const active = data.filter((c) => c.status === "active");
  const others = data.filter((c) => !isDemo(c) && c.status !== "active");

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Clienti</h1>
          <p className="text-sm text-slate-500 mt-1">I clienti in demo non generano provvigioni: maturano solo dopo che Lei conferma l'attivazione.</p>
        </div>
        <Link to="/portal/customers/new"><Button className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-new-customer">+ Nuovo cliente</Button></Link>
      </div>
      <div className="bg-white border border-[#E2DDD5] rounded-xl p-4 flex flex-wrap gap-6" data-testid="cust-summary">
        <div><div className="text-xs uppercase tracking-wider text-slate-500">In demo</div><div className="text-xl font-bold" data-testid="summary-demo">{demo.length}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-500">Attivi</div><div className="text-xl font-bold" data-testid="summary-active">{active.length}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-500">Provvigioni maturate sui clienti attivi</div><div className="text-xl font-bold text-emerald-700" data-testid="summary-commission-active">{formatEUR(dash?.commission_active_total)}</div></div>
      </div>
      {isLoading ? <p className="text-slate-500 text-sm">Caricamento…</p> : (
        <>
          <Section title="Clienti in demo" hint="Accesso con limitazioni, nessuna provvigione. A fine prova senza conferma il cliente viene sospeso." rows={demo} testid="section-demo" renderRow={row((c) => c.trial_end)} dateLabel="Fine prova" />
          <Section title="Clienti attivi" hint="Provvigioni maturate dalla data di conferma attivazione." rows={active} testid="section-active" renderRow={row((c) => c.license_end)} dateLabel="Licenza fino al" />
          {others.length > 0 && <Section title="In attesa e altri" rows={others} testid="section-others" renderRow={row((c) => c.license_end)} dateLabel="Licenza fino al" />}
        </>
      )}
    </div>
  );
}
